import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useChatContext } from '../context/ChatContext';
import { cn } from '../lib/utils';
import {
  formatMessageTime,
  formatMessageDate,
  formatMessageDay,
  formatMessageFullDateTime
} from '../utils/dateUtils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Send, UserPlus, Instagram, FileText, CheckCircle, CheckCheck, CircleAlert, Clock3, ExternalLink, LoaderCircle, Search, X, ChevronLeft, Info, CornerUpLeft, Smile, Paperclip, PhoneCall, UserRound, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { API, BACKEND_URL } from '../App';
import { useIsMobile, useIsTablet } from '../hooks/useMediaQuery';
import CreateInquiryModal from './CreateInquiryModal';
import './ChatWindow.css';
import BotContactPanel from './bots/BotContactPanel';
import { hasPermission } from '../utils/permissionUtils';

const STANDARD_RESPONSE_WINDOW_MS = 24 * 60 * 60 * 1000;
// Meta has not enabled outbound reactions, replies, or attachments for this app.
// Preserve those implementations for later; text, emoji, and approved templates remain available.
const ENABLE_OUTBOUND_MESSAGE_ACTIONS = false;
const ENABLE_COMPOSER_UTILITIES = true;
const EMOJI_OPTIONS = [
  '\u{1F600}', '\u{1F603}', '\u{1F604}', '\u{1F601}', '\u{1F606}', '\u{1F602}',
  '\u{1F642}', '\u{1F609}', '\u{1F60A}', '\u{1F60D}', '\u{1F618}', '\u{1F60E}',
  '\u{1F44D}', '\u{1F64F}', '\u{1F525}', '\u{1F389}', '\u2764\uFE0F', '\u{1F680}',
  '\u{1F31F}', '\u{1F916}', '\u{1F4CC}', '\u2705', '\u2753', '\u2615',
];
const MESSAGE_REACTION_OPTIONS = [
  { value: 'love', emoji: '❤️', label: 'Love' },
  { value: 'haha', emoji: '😂', label: 'Haha' },
  { value: 'wow', emoji: '😮', label: 'Wow' },
  { value: 'sad', emoji: '😢', label: 'Sad' },
  { value: 'angry', emoji: '😡', label: 'Angry' },
  { value: 'like', emoji: '👍', label: 'Like' },
];
const AUTOFILL_PROPS = {
  autoComplete: 'off',
  autoCorrect: 'off',
  autoCapitalize: 'off',
  spellCheck: false,
  name: 'no-autofill',
};
const getChatHandle = (chat) =>
  chat?.instagram_user?.username ||
  chat?.whatsapp_user?.phone_number ||
  chat?.username ||
  chat?.whatsapp_user_id ||
  chat?.instagram_user_id ||
  '';

const getChatDisplayName = (chat) =>
  chat?.instagram_user?.name ||
  chat?.instagram_user?.username ||
  chat?.whatsapp_user?.name ||
  chat?.whatsapp_user?.phone_number ||
  chat?.username ||
  'Unknown';

const extractTemplatePayload = (attachments = []) => {
  if (!Array.isArray(attachments)) {
    return null;
  }
  for (const attachment of attachments) {
    if (!attachment || attachment.type !== 'template') {
      continue;
    }
    const payload = attachment.payload || {};
    const elements =
      (payload.generic && payload.generic.elements) ||
      payload.elements ||
      [];
    if (!Array.isArray(elements) || elements.length === 0) {
      continue;
    }
    const [first] = elements;
    const buttons = Array.isArray(first?.buttons) ? first.buttons : [];
    return {
      title: first?.title || '',
      buttons,
    };
  }
  return null;
};

const getMessageSnippet = (message) => {
  if (!message) {
    return '';
  }
  const content = message.content === null || message.content === undefined
    ? ''
    : String(message.content).trim();
  if (content && content.toLowerCase() !== '[attachment]') {
    return content;
  }
  const templatePayload = extractTemplatePayload(message.attachments);
  if (templatePayload?.title) {
    return templatePayload.title;
  }
  if (Array.isArray(message.attachments) && message.attachments.length > 0) {
    return '[attachment]';
  }
  return '';
};

const splitFullName = (raw = '') => {
  const onlyAlnumSpaces = (raw || '').replace(/[^A-Za-z0-9\s]/g, '').trim();
  const cleaned = onlyAlnumSpaces.replace(/\s+/g, ' ');
  if (!cleaned) return { first: '', middle: '', last: '' };
  const parts = cleaned.split(' ');
  if (parts.length === 1) {
    return { first: parts[0], middle: '', last: '' };
  }
  if (parts.length === 2) {
    return { first: parts[0], middle: '', last: parts[1] };
  }
  return { first: parts[0], middle: parts.slice(1, -1).join(' '), last: parts[parts.length - 1] };
};

const FacebookIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const resolveAttachmentUrl = (attachment) => {
  const source = attachment?.public_url || attachment?.payload?.url || attachment?.url || '';
  if (!source) return null;
  if (source.startsWith('http://') || source.startsWith('https://')) return source;
  return `${BACKEND_URL || ''}${source}`;
};

const interpolateTemplateText = (value, variables = {}) => String(value || '').replace(
  /\{\{(\d+)\}\}/g,
  (_match, position) => variables[String(position)] ?? variables[position] ?? `{{${position}}}`,
);

// WhatsApp accepts the send request first, then reports a final delivery
// result asynchronously through the webhook. Meta puts the useful reason in
// an errors array, so turn its variable response shape into one safe label for
// the conversation rather than leaving operators with only a red icon.
const getWhatsAppFailureReason = (errors) => {
  const firstError = Array.isArray(errors) ? errors[0] : errors;
  if (!firstError || typeof firstError !== 'object') return '';
  const details = firstError.error_data?.details;
  const reason = details || firstError.message || firstError.title || '';
  return String(reason).trim().slice(0, 500);
};

const getWhatsAppTemplatePayload = (attachment) => {
  if (attachment?.type !== 'whatsapp_template') return null;
  const payload = attachment.payload || {};
  const components = Array.isArray(payload.components) ? payload.components : [];
  const variables = payload.variables || {};
  const getText = (type) => {
    const component = components.find((item) => String(item?.type || '').toUpperCase() === type);
    return component?.text ? interpolateTemplateText(component.text, variables) : '';
  };
  const buttonComponent = components.find((item) => String(item?.type || '').toUpperCase() === 'BUTTONS');
  return {
    name: payload.name || 'WhatsApp template',
    header: getText('HEADER'),
    body: getText('BODY'),
    footer: getText('FOOTER'),
    buttons: Array.isArray(buttonComponent?.buttons) ? buttonComponent.buttons : [],
  };
};

const getWhatsAppInteractivePayload = (attachment) => {
  if (attachment?.type !== 'whatsapp_interactive') return null;
  const payload = attachment.payload || {};
  const action = payload.action || {};
  if (payload.type === 'button') {
    return {
      type: 'button',
      buttons: (Array.isArray(action.buttons) ? action.buttons : []).map((button) => ({
        id: button?.reply?.id || '',
        title: button?.reply?.title || 'Option',
      })),
    };
  }
  if (payload.type === 'list') {
    return { type: 'list', buttons: [{ id: '', title: action.button || 'Choose' }] };
  }
  return null;
};

const ChatAvatar = ({ src, alt, className = 'w-10 h-10' }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  const showImage = src && !failed;
  return showImage ? (
    <img
      src={src}
      alt={alt}
      className={`${className} rounded-full object-cover border border-gray-700`}
      onError={() => setFailed(true)}
    />
  ) : (
    <div
      className={`${className} rounded-full border border-[var(--tg-border-soft)] bg-[var(--tg-surface-muted)] flex items-center justify-center text-sm font-semibold text-[var(--tg-text-muted)]`}
      title="No profile photo available"
    >
      {String(alt || 'U').trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'U'}
    </div>
  );
};

const MessageAttachment = ({ attachment, messageId, chatId, index, onPreview }) => {
  const [previewFailed, setPreviewFailed] = useState(false);
  const [whatsAppMediaUrl, setWhatsAppMediaUrl] = useState(null);
  const payload = attachment?.payload || {};
  const whatsAppMediaId = payload?.media_id;
  const attachmentUrl = whatsAppMediaUrl || resolveAttachmentUrl(attachment);
  const whatsAppTemplate = getWhatsAppTemplatePayload(attachment);
  const whatsAppInteractive = getWhatsAppInteractivePayload(attachment);
  useEffect(() => setPreviewFailed(false), [attachmentUrl]);
  useEffect(() => {
    let active = true;
    let objectUrl = null;
    if (!whatsAppMediaId || !chatId) {
      setWhatsAppMediaUrl(null);
      return undefined;
    }
    const token = localStorage.getItem('token');
    axios.get(`${API}/chats/${chatId}/whatsapp-media/${encodeURIComponent(whatsAppMediaId)}`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    }).then((response) => {
      if (!active) return;
      objectUrl = URL.createObjectURL(response.data);
      setWhatsAppMediaUrl(objectUrl);
    }).catch((error) => {
      console.warn('Unable to load WhatsApp media preview:', error.response?.data?.detail || error.message);
      if (active) setPreviewFailed(true);
    });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [chatId, whatsAppMediaId]);
  const attachmentType = String(attachment?.type || '').toLowerCase();
  const contentType = String(attachment?.content_type || '').toLowerCase();
  const isStoryMention = attachmentType === 'story_mention';
  const isStoryReply = attachmentType === 'story_reply';
  const isStoryContext = isStoryMention || isStoryReply;
  const isImage = isStoryContext || attachmentType === 'image' || attachmentType === 'gif' || contentType.startsWith('image/');
  const isVideo = attachmentType === 'video' || contentType.startsWith('video/');
  const shareUrl = payload?.share_url || payload?.permalink || attachment?.permalink ||
    (attachmentType === 'share' || attachmentType === 'reel' || attachmentType === 'post' ? attachmentUrl : null);
  const thumbnailUrl = payload?.thumbnail_url || payload?.media_url || attachment?.thumbnail_url || attachment?.media_url;
  const isSocialShare = Boolean(shareUrl) && /(?:instagram\.com|facebook\.com|fb\.watch)/i.test(shareUrl);
  const key = `${messageId || 'message'}-attachment-${index}`;

  if (whatsAppTemplate) {
    return (
      <section key={key} className="whatsapp-template-card" aria-label={`WhatsApp template ${whatsAppTemplate.name}`}>
        {whatsAppTemplate.header && <h4>{whatsAppTemplate.header}</h4>}
        <p>{whatsAppTemplate.body || whatsAppTemplate.name}</p>
        {whatsAppTemplate.footer && <small>{whatsAppTemplate.footer}</small>}
        {whatsAppTemplate.buttons.length > 0 && (
          <div className="whatsapp-structured-actions">
            {whatsAppTemplate.buttons.map((button, buttonIndex) => {
              const type = String(button?.type || '').toUpperCase();
              const label = button?.text || 'Action';
              const href = type === 'URL' && /^https?:\/\//i.test(String(button?.url || ''))
                ? button.url
                : type === 'PHONE_NUMBER' && button?.phone_number
                  ? `tel:${String(button.phone_number).replace(/[^\d+]/g, '')}`
                  : null;
              const Icon = type === 'PHONE_NUMBER' ? PhoneCall : ExternalLink;
              return href ? (
                <a key={`${type}-${buttonIndex}`} href={href} target={type === 'URL' ? '_blank' : undefined} rel={type === 'URL' ? 'noreferrer' : undefined} className="whatsapp-structured-action">
                  <Icon className="h-3.5 w-3.5" />{label}
                </a>
              ) : (
                <div key={`${type}-${buttonIndex}`} className="whatsapp-structured-action"><MessageCircle className="h-3.5 w-3.5" />{label}</div>
              );
            })}
          </div>
        )}
      </section>
    );
  }

  if (whatsAppInteractive) {
    return (
      <div key={key} className="whatsapp-structured-actions whatsapp-structured-actions--interactive" aria-label="WhatsApp reply options">
        {whatsAppInteractive.buttons.map((button, buttonIndex) => (
          <div key={`${button.id || 'option'}-${buttonIndex}`} className="whatsapp-structured-action whatsapp-structured-action--reply">
            <MessageCircle className="h-3.5 w-3.5" />{button.title}
          </div>
        ))}
      </div>
    );
  }

  if ((isImage || isVideo) && attachmentUrl && !previewFailed) {
    if (isVideo) {
      return (
        <video key={key} src={attachmentUrl} controls preload="metadata" className="max-h-80 w-full rounded-xl border border-white/10 bg-black" onError={() => setPreviewFailed(true)}>
          Your browser cannot play this video.
        </video>
      );
    }
    const alt = isStoryMention ? 'Instagram story mention' : (isStoryReply ? 'Instagram story reply' : 'Shared media');
    return (
      <button
        key={key}
        type="button"
        className={`message-media-preview-button ${isStoryContext ? 'max-w-[280px] aspect-[9/16]' : ''}`}
        onClick={() => onPreview?.({ src: attachmentUrl, alt })}
        title="Open image at full size"
        aria-label="Open image at full size"
      >
        <img
          src={attachmentUrl}
          alt={alt}
          className="message-media-preview max-h-80 w-full rounded-xl object-cover border border-white/10"
          onError={() => setPreviewFailed(true)}
        />
      </button>
    );
  }

  if (isSocialShare) {
    return (
      <a key={key} href={shareUrl} target="_blank" rel="noreferrer" className="social-share-preview">
        {thumbnailUrl && !previewFailed ? (
          <img src={thumbnailUrl} alt="Shared post preview" className="social-share-preview__image" onError={() => setPreviewFailed(true)} />
        ) : (
          <div className="social-share-preview__placeholder"><Instagram className="w-4 h-4" /> Shared {attachmentType === 'reel' ? 'reel' : 'post'}</div>
        )}
        <span>Open shared {attachmentType === 'reel' ? 'reel' : 'post'}</span>
      </a>
    );
  }

  if (attachmentUrl) {
    return (
      <a key={key} href={attachmentUrl} target="_blank" rel="noreferrer" className="attachment-fallback-card">
        <FileText className="w-4 h-4" />
        <span>{isStoryContext ? 'Story preview is no longer available' : 'Open attachment'}</span>
      </a>
    );
  }
  return null;
};

const getSenderLabel = (message, chat) => {
  const sender = (message?.sender || '').toString().toLowerCase();
  if (sender === 'agent' || sender === 'instagram_page' || sender === 'whatsapp_business') {
    return 'You';
  }
  if (message?.sent_by?.name) {
    return message.sent_by.name;
  }
  return (
    chat?.instagram_user?.name ||
    chat?.facebook_user?.name ||
    chat?.whatsapp_user?.name ||
    chat?.whatsapp_user?.phone_number ||
    chat?.username ||
    'User'
  );
};

const ChatWindow = ({
  agents,
  userRole,
  onAssignChat,
  canAssignChats = false,
  onBackToList,
  showAssignmentInfo = true,
  currentUser = null,
}) => {
  const {
    selectedChat: chat,
    sendMessage,
    selectChat,
    updateChatMessages,
    updateMessageReactions,
    outboxMessages,
    loadOlderMessages,
    isLoadingOlderMessages,
  } = useChatContext();
  const supportsHumanAgent = chat?.platform === 'FACEBOOK' || chat?.platform === 'INSTAGRAM';
  const isWhatsAppChat = chat?.platform === 'WHATSAPP';
  const isMobile = useIsMobile(); // Must be at top level before any conditional logic
  const isTablet = useIsTablet();

  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateVariables, setTemplateVariables] = useState({});
  const [quickReplies, setQuickReplies] = useState([]);
  const [showQuickReplyDialog, setShowQuickReplyDialog] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [replyTarget, setReplyTarget] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [reactionPickerMessageId, setReactionPickerMessageId] = useState(null);
  const [reactingMessageId, setReactingMessageId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [createInquiryPrefill, setCreateInquiryPrefill] = useState({});
  const [humanAgentEligibility, setHumanAgentEligibility] = useState(null);
  const [showHumanAgentDialog, setShowHumanAgentDialog] = useState(false);
  const [humanAgentContent, setHumanAgentContent] = useState('');
  const [humanAgentReason, setHumanAgentReason] = useState('');
  const [humanAgentAcknowledged, setHumanAgentAcknowledged] = useState(false);
  const [isSendingHumanAgent, setIsSendingHumanAgent] = useState(false);
  const [humanAgentSuccess, setHumanAgentSuccess] = useState('');
  const loadingOlderMessagesRef = useRef(false);
  const [mediaPreview, setMediaPreview] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const messageRefs = useRef({});
  const messageRefCallbacks = useRef({});
  const emojiPopoverRef = useRef(null);
  const quickReplySuggestionRef = useRef(null);
  const mediaInputRef = useRef(null);
  const composerInputRef = useRef(null);
  const chatHandle = useMemo(() => getChatHandle(chat), [chat]);
  const chatDisplayName = useMemo(() => getChatDisplayName(chat), [chat]);
  const chatAvatarUrl = useMemo(() => {
    if (!chat) {
      return null;
    }
    return chat.profile_pic_url || chat.profile_pic || chat.whatsapp_user?.profile_pic_url || null;
  }, [chat]);

  const quickReplySuggestion = useMemo(() => {
    if (!isWhatsAppChat || !message.trim() || message.includes('\n')) return null;
    const typed = message.trimStart().replace(/^\//, '').toLowerCase();
    if (!typed) return null;
    return quickReplies.find((reply) => reply.content.toLowerCase().startsWith(typed)) ||
      quickReplies.find((reply) => (
        reply.shortcut.toLowerCase().startsWith(typed) ||
        reply.name.toLowerCase().startsWith(typed) ||
        reply.name.toLowerCase().includes(typed)
      )) || null;
  }, [isWhatsAppChat, message, quickReplies]);

  const quickReplySuggestionText = useMemo(() => {
    if (!quickReplySuggestion) return '';
    const typed = message.trimStart();
    return quickReplySuggestion.content.toLowerCase().startsWith(typed.toLowerCase())
      ? quickReplySuggestion.content.slice(typed.length)
      : quickReplySuggestion.content;
  }, [message, quickReplySuggestion]);

  const resizeComposer = useCallback(() => {
    window.requestAnimationFrame(() => {
      const input = composerInputRef.current;
      if (!input) return;
      input.style.height = 'auto';
      const suggestionHeight = quickReplySuggestionRef.current?.scrollHeight || 0;
      input.style.height = `${Math.min(Math.max(input.scrollHeight, suggestionHeight), 180)}px`;
    });
  }, []);

  const assignedAgentName = useMemo(() => {
    if (!chat) {
      return null;
    }
    return (
      chat.assigned_agent?.name ||
      chat.assigned_agent?.email ||
      chat.assigned_to_name ||
      chat.assigned_to ||
      null
    );
  }, [chat]);

  const lastActivityLabel = useMemo(() => {
    if (!chat) {
      return '';
    }
    const timestamps = [
      chat.last_message_timestamp,
      chat.last_message_at,
      chat.last_activity_at,
      chat.last_incoming_at,
      chat.last_outgoing_at,
    ]
      .map((value) => (value ? new Date(value).getTime() : Number.NaN))
      .filter((value) => !Number.isNaN(value));
    const timestamp = timestamps.length
      ? new Date(Math.max(...timestamps)).toISOString()
      : chat.created_at;
    if (!timestamp) {
      return '';
    }
    return formatMessageFullDateTime(timestamp);
  }, [chat]);

  useEffect(() => {
    scrollToBottom();
  }, [chat?.id]);

  useEffect(() => {
    setReplyTarget(null);
    setReactionPickerMessageId(null);
    setHumanAgentSuccess('');
    messageRefs.current = {};
    messageRefCallbacks.current = {};
  }, [chat?.id]);
  const startReply = useCallback((targetMessage) => {
    if (!targetMessage?.id) return;
    setReplyTarget({
      id: targetMessage.id,
      author: getSenderLabel(targetMessage, chat),
      snippet: getMessageSnippet(targetMessage) || '[attachment]',
    });
    window.requestAnimationFrame(() => composerInputRef.current?.focus());
  }, [chat]);

  const handleMessageTouchStart = useCallback((targetMessage) => {
    if (!isMobile) {
      return;
    }
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }
    longPressTimerRef.current = setTimeout(() => {
      startReply(targetMessage);
    }, 600);
  }, [isMobile, startReply]);
  const cancelTouchTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => cancelTouchTimer(), [cancelTouchTimer]);

  const handleReplyClick = useCallback((event, targetMessage) => {
    event?.stopPropagation();
    startReply(targetMessage);
  }, [startReply]);

  const handleCancelReply = useCallback(() => {
    setReplyTarget(null);
  }, []);

  const handleMessageReaction = useCallback(async (event, targetMessage, reaction) => {
    event?.stopPropagation();
    if (!chat || chat.platform !== 'INSTAGRAM' || !targetMessage?.id || reactingMessageId) {
      return;
    }
    setReactingMessageId(targetMessage.id);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/chats/${chat.id}/messages/${targetMessage.id}/reaction`,
        { reaction },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      updateMessageReactions(chat.id, targetMessage.id, response.data.reactions || []);
      setReactionPickerMessageId(null);
    } catch (error) {
      console.error('Unable to react to message:', error);
      alert(error.response?.data?.detail || 'Unable to react to this message.');
    } finally {
      setReactingMessageId(null);
    }
  }, [chat, reactingMessageId, updateMessageReactions]);

  const handleQuotedMessageJump = useCallback((messageId) => {
    if (!messageId) return;
    const node = messageRefs.current[messageId];
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      node.classList.add('message-highlight');
      setTimeout(() => {
        node.classList.remove('message-highlight');
      }, 1200);
    }
  }, []);

  const setMessageNodeRef = useCallback((id) => {
    if (!id) return undefined;
    if (!messageRefCallbacks.current[id]) {
      messageRefCallbacks.current[id] = (element) => {
        if (element) {
          messageRefs.current[id] = element;
        } else {
          delete messageRefs.current[id];
        }
      };
    }
    return messageRefCallbacks.current[id];
  }, []);

  const orderedMessages = useMemo(() => {
    const persistedMessages = Array.isArray(chat?.messages) ? chat.messages : [];
    const pendingMessages = (outboxMessages || [])
      .filter((entry) => String(entry.chat_id) === String(chat?.id))
      .map((entry) => ({
        id: `outbox:${entry.id}`,
        chat_id: entry.chat_id,
        sender: chat?.platform === 'WHATSAPP' ? 'WHATSAPP_BUSINESS' : 'AGENT',
        content: entry.content,
        timestamp: entry.created_at,
        is_ticklegram: true,
        metadata: {
          outbox_status: entry.status,
          outbox_error: entry.last_error,
          client_message_id: entry.id,
        },
      }));
    const seenIds = new Set();
    return [...persistedMessages, ...pendingMessages]
      .filter((msg) => {
        if (!msg?.id) return true;
        if (seenIds.has(msg.id)) return false;
        seenIds.add(msg.id);
        return true;
      })
      .sort((a, b) => {
        const tsA = (() => {
          if (a?.timestamp) {
            const v = new Date(a.timestamp).getTime();
            if (!Number.isNaN(v)) return v;
          }
          if (typeof a?.ts === 'number') return a.ts * 1000;
          return 0;
        })();
        const tsB = (() => {
          if (b?.timestamp) {
            const v = new Date(b.timestamp).getTime();
            if (!Number.isNaN(v)) return v;
          }
          if (typeof b?.ts === 'number') return b.ts * 1000;
          return 0;
        })();
        return tsA - tsB; // oldest -> newest
      });
  }, [chat?.id, chat?.messages, chat?.platform, outboxMessages]);

  const conversationTimeline = useMemo(() => {
    const events = Array.isArray(chat?.assignment_history) ? chat.assignment_history : [];
    const timestampOf = (item) => {
      const value = item?.timestamp || item?.created_at;
      const parsed = value ? new Date(value).getTime() : 0;
      return Number.isNaN(parsed) ? 0 : parsed;
    };
    return [
      ...orderedMessages.map((message) => ({ kind: 'message', message, timestamp: message.timestamp || (message.ts ? new Date(message.ts * 1000).toISOString() : null) })),
      ...events.map((event) => ({ kind: 'assignment', event, timestamp: event.created_at })),
    ].sort((left, right) => timestampOf(left) - timestampOf(right));
  }, [chat?.assignment_history, orderedMessages]);

  useLayoutEffect(() => {
    // Prepending an older page must preserve the reader's visual position.
    // New messages and a newly selected chat continue to scroll to the latest
    // entry as before.
    if (!loadingOlderMessagesRef.current) {
      scrollToBottom();
    }
  }, [conversationTimeline]);

  const handleLoadOlderMessages = useCallback(async () => {
    const container = messagesContainerRef.current;
    const previousHeight = container?.scrollHeight || 0;
    const previousTop = container?.scrollTop || 0;
    loadingOlderMessagesRef.current = true;
    try {
      await loadOlderMessages();
      window.requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = previousTop + (container.scrollHeight - previousHeight);
        }
        loadingOlderMessagesRef.current = false;
      });
    } catch (error) {
      loadingOlderMessagesRef.current = false;
      throw error;
    }
  }, [loadOlderMessages]);

  // Textarea values may also be changed programmatically (for example when a
  // quick reply is accepted), so resize from the value rather than only from
  // the browser's input event.
  useLayoutEffect(() => {
    resizeComposer();
  }, [message, resizeComposer]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Load templates when dialog opens
  useEffect(() => {
    if (showTemplateDialog && chat) {
      loadTemplates();
    }
  }, [showTemplateDialog, chat]);

  // Filter templates based on search
  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = templates.filter(t => 
        t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.content.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredTemplates(filtered);
    } else {
      setFilteredTemplates(templates);
    }
  }, [searchQuery, templates]);

  // Keyboard shortcut for template selector (Ctrl+T or Cmd+T)
  useEffect(() => {
    if (!ENABLE_COMPOSER_UTILITIES) {
      return undefined;
    }
    const handleKeyPress = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 't') {
        e.preventDefault();
        if (chat) {
          setShowTemplateDialog(true);
        }
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [chat, isWhatsAppChat]);

  useEffect(() => {
    if (!chat && isProfileOpen) {
      setIsProfileOpen(false);
    }
  }, [chat, isProfileOpen]);

  useEffect(() => () => cancelTouchTimer(), [cancelTouchTimer]);

  useEffect(() => {
    const handleOutside = (event) => {
      if (!showEmojiPicker) return;
      if (emojiPopoverRef.current && !emojiPopoverRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [showEmojiPicker]);

  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/templates`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { platform: chat.platform }
      });
      // Pending/rejected WhatsApp templates are useful in the admin manager but
      // cannot be delivered by Cloud API, so never offer them in a chat.
      const availableTemplates = chat.platform === 'WHATSAPP'
        ? response.data.filter((template) => template.is_meta_approved)
        : response.data;
      setTemplates(availableTemplates);
      setFilteredTemplates(availableTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadQuickReplies = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/quick-replies`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setQuickReplies(response.data);
    } catch (error) {
      console.warn('Unable to load quick replies:', error);
    }
  }, []);

  useEffect(() => {
    if (!isWhatsAppChat) {
      setQuickReplies([]);
      setShowQuickReplyDialog(false);
      return;
    }
    loadQuickReplies();
  }, [chat?.id, isWhatsAppChat, loadQuickReplies]);

  const applyQuickReply = (reply) => {
    setMessage(reply.content);
    setShowQuickReplyDialog(false);
    resizeComposer();
    window.requestAnimationFrame(() => composerInputRef.current?.focus());
  };

const extractVariables = (content) => {
  const regex = /\{\{(\d+)\}\}|\{([^}]+)\}/g;
  const variables = [];
  let match;
  while ((match = regex.exec(content)) !== null) {
    const varName = match[1] || match[2];
    if (!varName) continue;
    variables.push(varName);
  }
  return [...new Set(variables)]; // Remove duplicates
};

  const previewTemplateContent = (template) => {
    if (!template) return '';
    let content = template.content;
    const chatHandle = getChatHandle(chat);
    // Auto-populate
    const replacements = {
      username: chatHandle,
      platform: chat.platform,
      'user.name': chat?.instagram_user?.name || chat?.facebook_user?.name || chat?.whatsapp_user?.name || chat?.username || chatHandle,
      'user.handle': chatHandle,
    };
    Object.entries(replacements).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        content = content.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
      }
    });
    // User-provided variables
    Object.keys(templateVariables).forEach(key => {
      const val = templateVariables[key];
      if (val) {
        content = content.replace(new RegExp(`\\{${key}\\}`, 'g'), val);
      }
    });
    return content;
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    const vars = extractVariables(template.content);
    const initialVars = {};
    vars.forEach((v) => {
      // Pre-fill common variables with best-effort values while keeping structure unchanged
      if (v === 'username') {
        initialVars[v] = getChatHandle(chat);
      } else if (v === 'name') {
        initialVars[v] = chat?.instagram_user?.name || chat?.facebook_user?.name || chat?.whatsapp_user?.name || chat?.username || '';
      } else if (v === 'agent_name') {
        initialVars[v] = currentUser?.name || currentUser?.email || '';
      } else {
        initialVars[v] = '';
      }
    });
    setTemplateVariables(initialVars);
  };

  const lastCustomerMessageTimestamp = useMemo(() => {
    if (!Array.isArray(chat?.messages) || chat.messages.length === 0) {
      return null;
    }

    for (let i = chat.messages.length - 1; i >= 0; i -= 1) {
      const message = chat.messages[i];
      if (!message) {
        continue;
      }
      const sender = (message.sender || '').toString().toLowerCase();
      if (sender !== 'agent' && sender !== 'instagram_page') {
        if (!message.timestamp) {
          return null;
        }
        const time = new Date(message.timestamp).getTime();
        return Number.isNaN(time) ? null : time;
      }
    }

    return null;
  }, [chat?.messages]);

  const canSendManualMessage = useMemo(() => {
    if (!supportsHumanAgent && !isWhatsAppChat) {
      return true;
    }
    if (!lastCustomerMessageTimestamp) {
      // A WhatsApp chat is opened by an inbound customer event, so a missing
      // timestamp is not a safe reason to bypass the service-window policy.
      return !isWhatsAppChat;
    }
    const now = Date.now();
    return now - lastCustomerMessageTimestamp <= STANDARD_RESPONSE_WINDOW_MS;
  }, [isWhatsAppChat, lastCustomerMessageTimestamp, supportsHumanAgent]);

  const show24hExpiryNotice = (supportsHumanAgent || isWhatsAppChat) && !canSendManualMessage;

  useEffect(() => {
    let cancelled = false;
    if (!chat?.id || !supportsHumanAgent) {
      setHumanAgentEligibility(null);
      return undefined;
    }
    const token = localStorage.getItem('token');
    axios
      .get(`${API}/chats/${chat.id}/human-agent-eligibility`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (!cancelled) setHumanAgentEligibility(response.data);
      })
      .catch((error) => {
        // Eligibility is supplementary UI data; normal send protections remain
        // server-authoritative even if this request is temporarily unavailable.
        console.warn('Unable to load Human Agent eligibility:', error);
        if (!cancelled) setHumanAgentEligibility(null);
      });
    return () => {
      cancelled = true;
    };
  }, [chat?.id, supportsHumanAgent]);

  useEffect(() => {
    if (!canSendManualMessage) {
      setMessage('');
    }
  }, [canSendManualMessage]);

  const handleSendTemplate = async () => {
    if (!selectedTemplate) return;
    if (!canSendManualMessage && !selectedTemplate.is_meta_approved) {
      alert('Only Meta-approved templates can be sent outside the 24-hour window.');
      return;
    }

    setIsSending(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        chat_id: chat.id,
        variables: templateVariables,
      };
      if (replyTarget?.id) {
        payload.reply_to_message_id = replyTarget.id;
        if (replyTarget.snippet) {
          payload.reply_preview = replyTarget.snippet;
        }
      }
      await axios.post(
        `${API}/templates/${selectedTemplate.id}/send`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setShowTemplateDialog(false);
      setSelectedTemplate(null);
      setTemplateVariables({});
      setSearchQuery('');
      if (replyTarget) {
        setReplyTarget(null);
      }
    } catch (error) {
      console.error('Error sending template:', error);
      const message = error.response?.data?.detail || 'Failed to send template';
      alert(message);
    } finally {
      setIsSending(false);
    }
  };

  const handleSend = useCallback(async (e) => {
    e.preventDefault();
    if (!chat || (!message.trim() && attachments.length === 0) || isSending || !canSendManualMessage) {
      return;
    }

    setIsSending(true);
    try {
      await sendMessage(chat.id, message, { attachments, platform: chat.platform });
      setMessage('');
      setAttachments([]);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSending(false);
    }
  }, [message, chat, sendMessage, isSending, canSendManualMessage, attachments]);

  const handleSendHumanAgentMessage = useCallback(async (event) => {
    event.preventDefault();
    if (
      !chat?.id ||
      !humanAgentEligibility?.eligible ||
      !humanAgentContent.trim() ||
      !humanAgentReason.trim() ||
      !humanAgentAcknowledged ||
      isSendingHumanAgent
    ) {
      return;
    }

    setIsSendingHumanAgent(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/chats/${chat.id}/human-agent-message`,
        {
          content: humanAgentContent.trim(),
          support_reason: humanAgentReason.trim(),
          policy_acknowledged: true,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      updateChatMessages(chat.id, response.data);
      setShowHumanAgentDialog(false);
      setHumanAgentContent('');
      setHumanAgentReason('');
      setHumanAgentAcknowledged(false);
      setHumanAgentEligibility((current) => current ? { ...current, eligible: false } : current);
      setHumanAgentSuccess('Human Agent support response sent successfully and recorded in the audit trail.');
    } catch (error) {
      console.error('Unable to send Human Agent response:', error);
      alert(error.response?.data?.detail || 'Unable to send the Human Agent response.');
    } finally {
      setIsSendingHumanAgent(false);
    }
  }, [chat?.id, humanAgentAcknowledged, humanAgentContent, humanAgentEligibility?.eligible, humanAgentReason, isSendingHumanAgent, updateChatMessages]);

  const handleMediaSelected = useCallback(async (event) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = '';
    if (!selectedFile || !chat || isUploadingMedia) return;

    const acceptedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'video/mp4', 'video/quicktime', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!acceptedTypes.includes(selectedFile.type)) {
      alert('Choose an image, video, PDF, DOC, or DOCX file.');
      return;
    }
    if (selectedFile.size > 25 * 1024 * 1024) {
      alert('Media must be 25 MB or smaller.');
      return;
    }

    setIsUploadingMedia(true);
    try {
      const token = localStorage.getItem('token');
      const body = new FormData();
      body.append('file', selectedFile);
      const response = await axios.post(`${API}/chats/${chat.id}/attachments`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAttachments([response.data.attachment]);
    } catch (error) {
      console.error('Unable to upload media:', error);
      alert(error.response?.data?.detail || 'Unable to upload media.');
    } finally {
      setIsUploadingMedia(false);
    }
  }, [chat, isUploadingMedia]);

  const handleProfileToggle = useCallback(() => {
    if (!chat) {
      return;
    }
    setIsProfileOpen((prev) => !prev);
  }, [chat]);

  const handleCloseProfile = useCallback(() => {
    setIsProfileOpen(false);
  }, []);

  const showMobileBackButton = Boolean(onBackToList) && isMobile;

  if (!chat) {
    return (
      <div className="conversation-panel h-full w-full flex items-center justify-center" data-testid="no-chat-selected">
        <div className="text-center space-y-2">
          <MessageSquareIcon className="w-16 h-16 text-gray-600 mx-auto" />
          <p className="text-[var(--tg-text-secondary)] text-lg">Select a chat to start messaging</p>
        </div>
      </div>
    );
  }
  return (
    <div
      className={cn(
        'flex h-full min-h-0 w-full gap-3 overflow-hidden',
        isMobile ? 'flex-col' : 'items-stretch'
      )}
      data-testid="chat-window"
    >
      <div className="conversation-panel flex-1 relative w-full min-w-0 flex flex-col">
        {/* Chat Header */}
        <div className="conversation-header">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {showMobileBackButton && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="px-2 text-[var(--tg-text-primary)] hover:text-[var(--tg-accent-strong)] rounded-full bg-[var(--tg-surface-muted)]/60"
                onClick={onBackToList}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                <span className="text-sm font-medium">Back</span>
              </Button>
            )}
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <ChatAvatar src={chatAvatarUrl} alt={chatDisplayName} />
              <div className="flex flex-col gap-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 min-w-0">
                  <button
                    type="button"
                    onClick={handleProfileToggle}
                    className="text-sm sm:text-base font-semibold text-white hover:text-[var(--tg-accent-strong)] transition-colors truncate max-w-[200px] sm:max-w-[260px]"
                    data-testid="chat-username"
                    title="View profile info"
                  >
                    {chatDisplayName}
                  </button>
                  <span className="assignment-pill assignment-pill--compact inline-flex items-center gap-1">
                    {chat.platform === 'FACEBOOK' ? (
                      <FacebookIcon className="w-3.5 h-3.5" />
                    ) : chat.platform === 'WHATSAPP' ? (
                      <MessageCircle className="w-3.5 h-3.5" />
                    ) : (
                      <Instagram className="w-3.5 h-3.5" />
                    )}
                    {chat.platform === 'FACEBOOK' ? 'Messenger' : chat.platform === 'WHATSAPP' ? 'WhatsApp' : 'Instagram'}
                  </span>
                  {chat.platform === 'INSTAGRAM' && chat.inbox_account_name ? (
                    <span
                      className="assignment-pill assignment-pill--compact inline-flex max-w-[160px] truncate text-[11px]"
                      title={`Instagram account: @${String(chat.inbox_account_name).replace(/^@/, '')}`}
                    >
                      @{String(chat.inbox_account_name).replace(/^@/, '')}
                    </span>
                  ) : null}
                  {chat.is_ad_lead ? <span className="chat-ad-lead-badge chat-ad-lead-badge--header">AD Lead</span> : null}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-[var(--tg-text-muted)] min-w-0">
                  <span className="truncate">@{chatHandle || 'unknown'}</span>
                  {lastActivityLabel && (
                    <span className="hidden sm:inline-flex items-center gap-1 text-[var(--tg-text-muted)]">
                      <span className="inline-block w-1 h-1 rounded-full bg-[var(--tg-border-soft)]" />
                      <span className="truncate">{lastActivityLabel}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap justify-end w-full md:w-auto md:flex-none">
            {canAssignChats ? (
              <Select
                value={chat.assigned_to || 'unassigned'}
                onValueChange={(value) => onAssignChat(chat.id, value === 'unassigned' ? null : value)}
              >
                <SelectTrigger
                  className="relative h-9 w-[190px] rounded-full border-[var(--tg-border-soft)] bg-[var(--tg-surface)] pl-9 pr-3 text-sm font-medium text-[var(--tg-text-primary)] shadow-none hover:border-[var(--tg-accent)]/60 focus:ring-1 focus:ring-[var(--tg-accent)]"
                  data-testid="assign-agent-select"
                  aria-label="Assign chat"
                >
                  <UserPlus className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--tg-text-muted)]" />
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent className="z-[100] max-h-72 rounded-xl border-[var(--tg-border-soft)] bg-[#151b2a] p-1.5 text-[var(--tg-text-primary)] shadow-2xl shadow-black/45">
                  <SelectItem
                    value="unassigned"
                    className="rounded-lg py-2 pl-3 pr-9 font-medium text-[var(--tg-text-secondary)] focus:bg-[var(--tg-surface-raised)] focus:text-white"
                  >
                    Unassigned
                  </SelectItem>
                  {agents.map((agent) => (
                    <SelectItem
                      key={agent.id}
                      value={agent.id}
                      disabled={!agent.is_active}
                      className="rounded-lg py-2 pl-3 pr-9 font-medium text-[var(--tg-text-primary)] focus:bg-[var(--tg-accent)]/20 focus:text-white data-[disabled]:opacity-40"
                    >
                      {agent.name}{agent.is_active ? '' : ' (inactive)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              showAssignmentInfo && (
              <span
                className={cn(
                  'assignment-pill assignment-pill--compact text-xs px-3 py-1.5 rounded-full truncate max-w-[220px]',
                  assignedAgentName ? '' : 'assignment-pill--unassigned'
                )}
                data-testid="assigned-agent-readonly"
              >
                {assignedAgentName ? `Assigned to ${assignedAgentName}` : 'Unassigned'}
              </span>
              )
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-9 px-2.5 rounded-full border border-[var(--tg-border-soft)] bg-[var(--tg-surface-muted)]/70 flex items-center gap-1.5 text-[var(--tg-text-primary)] hover:text-[var(--tg-accent-strong)]"
              aria-label="Conversation details"
              onClick={handleProfileToggle}
            >
              <Info className="w-4 h-4" />
              <span className="text-sm hidden sm:inline">{isProfileOpen ? 'Hide details' : 'Details'}</span>
            </Button>
          </div>
        </div>
        {/* Messages */}
        <div className="conversation-body space-y-1.5 chat-scroll" data-testid="messages-container" ref={messagesContainerRef}>
          {chat.messages_has_more && (
            <div className="flex justify-center py-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={isLoadingOlderMessages}
                onClick={handleLoadOlderMessages}
                className="h-8 rounded-full border border-[var(--tg-border-soft)] bg-[var(--tg-surface-muted)] px-3 text-xs text-[var(--tg-text-secondary)] hover:bg-[var(--tg-surface-raised)] hover:text-white"
              >
                {isLoadingOlderMessages ? 'Loading older messages...' : 'Load older messages'}
              </Button>
            </div>
          )}
          {conversationTimeline.length === 0 ? (
            <div className="text-center text-gray-500 mt-8">No messages yet</div>
          ) : (
            (() => {
              let lastRenderedDay = null;
              return conversationTimeline.map((timelineItem, index) => {
                const entryTimestamp = timelineItem.timestamp;
                const entryDayLabel = entryTimestamp ? formatMessageDay(entryTimestamp) : '';
                const showEntryDayLabel = entryDayLabel && entryDayLabel !== lastRenderedDay;
                if (showEntryDayLabel) {
                  lastRenderedDay = entryDayLabel;
                }
                if (timelineItem.kind === 'assignment') {
                  const event = timelineItem.event;
                  const assignedTo = event.assigned_to_name || 'Unassigned';
                  const assignedBy = event.assigned_by_name || 'System';
                  const text = event.assigned_to_user_id
                    ? `Conversation assigned to ${assignedTo} by ${assignedBy}`
                    : `Conversation unassigned${event.previous_assigned_to_name ? ` from ${event.previous_assigned_to_name}` : ''} by ${assignedBy}`;
                  return (
                    <React.Fragment key={`assignment-${event.id || index}`}>
                      {showEntryDayLabel && (
                        <div className="text-center text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          {entryDayLabel}
                        </div>
                      )}
                      <div className="flex justify-center py-2" data-testid="assignment-history-event">
                        <div className="max-w-[88%] rounded-lg border border-[var(--tg-border-soft)] bg-[var(--tg-surface-raised)] px-3 py-2 text-center text-xs text-[var(--tg-text-secondary)] shadow-sm">
                          <span>{text}</span>
                          {event.created_at && <span className="ml-2 whitespace-nowrap text-[10px] text-[var(--tg-text-muted)]">{formatMessageTime(event.created_at)}</span>}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                }
                const msg = timelineItem.message;
                const senderType = (msg.sender || '').toString().toLowerCase();
                const isInstagramPage = senderType === 'instagram_page';
                const isAgentMessage = senderType === 'agent' || isInstagramPage || senderType === 'whatsapp_business';
                const isTicklegramMessage = Boolean(msg.is_ticklegram);
                const showAsTicklegram = isTicklegramMessage && isAgentMessage;
                const platformBubbleClass = `bubble-agent--${String(chat?.platform || '').toLowerCase()}`;
                const bubbleClasses = showAsTicklegram
                  ? `bubble-agent ${platformBubbleClass}`
                  : isInstagramPage
                    ? 'bubble-agent-external'
                    : isAgentMessage
                      ? 'bubble-agent'
                      : 'bubble-user';
                const sentByName =
                  (msg.sent_by?.id && currentUser?.id === msg.sent_by.id
                    ? currentUser.name || currentUser.email
                    : msg.sent_by?.name || msg.sent_by?.email);
                const outboxStatus = String(msg.metadata?.outbox_status || '').toLowerCase();
                let originLabel = '';
                if (outboxStatus === 'queued') {
                  originLabel = 'Queued - waiting for internet';
                } else if (outboxStatus === 'sending') {
                  originLabel = 'Sending...';
                } else if (outboxStatus === 'failed') {
                  originLabel = `Not sent${msg.metadata?.outbox_error ? ` - ${msg.metadata.outbox_error}` : ''}`;
                } else if (showAsTicklegram) {
                  originLabel = `Sent from Ticklegram${sentByName ? ` - ${sentByName}` : ''}`;
                } else if (isInstagramPage) {
                  originLabel = 'Sent from Instagram app';
                } else if (msg.metadata?.source === 'facebook_page_echo') {
                  originLabel = 'Sent from Messenger app';
                } else if (sentByName) {
                  originLabel = `Sent by ${sentByName}`;
                }
                const attachments = Array.isArray(msg.attachments) ? msg.attachments : [];
                const templatePayload = extractTemplatePayload(attachments);
                const hasWhatsAppTemplate = attachments.some((attachment) => Boolean(getWhatsAppTemplatePayload(attachment)));
                const hasStoryMention = attachments.some(
                  (attachment) => attachment && typeof attachment === 'object' && attachment.type === 'story_mention'
                );
                const hasStoryReply = attachments.some(
                  (attachment) => attachment && typeof attachment === 'object' && attachment.type === 'story_reply'
                );
                const hasAttachments = attachments.length > 0 && !templatePayload;
                const messageContent = msg.content === null || msg.content === undefined
                  ? ''
                  : String(msg.content);
                const placeholderText = messageContent.trim().toLowerCase();
                if (chat?.platform === 'WHATSAPP' && placeholderText === '[reaction]') {
                  // Legacy versions stored reaction webhooks as messages. Keep
                  // old data out of the transcript while new webhooks update
                  // the original message's reaction cluster instead.
                  return null;
                }
                const isMediaPlaceholder = /^\[(?:image|video|audio|document|sticker|gif)\]$/.test(placeholderText);
                // A WhatsApp template has structured header/body/footer content in
                // its attachment. Its `content` field is only retained for search
                // and the conversation preview, so never render it a second time.
                const hideText = hasWhatsAppTemplate || ((hasAttachments || templatePayload) && (
                  placeholderText === '[attachment]' || placeholderText === '' || isMediaPlaceholder
                ));
                const displayText = hideText ? '' : messageContent;
                // Older webhook processing could persist delivery-like events
                // with neither text nor an attachment. They have no useful
                // conversation content, so do not render an empty bubble.
                if (!displayText && attachments.length === 0 && !templatePayload) {
                  return null;
                }
                const phoneMatch = (() => {
                  if (!displayText) return null;
                  const match = displayText.match(/\+?\d[\d\s().-]{6,}\d/);
                  if (!match) return null;
                  const normalized = match[0].replace(/[^\d+]/g, '');
                  return normalized;
                })();
                const replyMetadata = msg.metadata || {};
                const whatsappFailureReason = getWhatsAppFailureReason(replyMetadata.whatsapp_errors);
                const isWhatsAppDeliveryFailure = (
                  chat?.platform === 'WHATSAPP' &&
                  String(replyMetadata.whatsapp_status || '').toLowerCase() === 'failed'
                );
                const isHumanAgentMessage = Boolean(replyMetadata?.human_agent?.used);
                const hasReplyContext = Boolean(replyMetadata.reply_to);
                const replyPlatform = String(chat?.platform || '').toLowerCase();
                // Older reply records used the product's previous internal label.
                // Render it consistently without needing to rewrite old chat data.
                const replySenderLabel = String(replyMetadata.reply_sender || '').trim().toLowerCase() === 'ticklegram'
                  ? 'TickleRight'
                  : (replyMetadata.reply_sender || 'Replying to a message');
                const canReplyToMessage = !isAgentMessage && Boolean(msg.id);
                const isReactableInstagramMessage = (
                  chat?.platform === 'INSTAGRAM' &&
                  Boolean(replyMetadata.instagram_mid || replyMetadata.meta_message_id)
                );
                const messageReactions = Array.isArray(msg.reactions) ? msg.reactions : [];

                return (
                  <React.Fragment key={msg.id || `${index}-${msg.timestamp || 'message'}`}>
                    {showEntryDayLabel && (
                      <div className="text-center text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {entryDayLabel}
                      </div>
                    )}
                    <div
                      className={`message-bubble group flex pb-4 ${isAgentMessage ? 'justify-end' : 'justify-start'}`}
                      data-testid={`message-${msg.id}`}
                      ref={setMessageNodeRef(msg.id)}
                      onTouchStart={ENABLE_OUTBOUND_MESSAGE_ACTIONS && canReplyToMessage ? () => handleMessageTouchStart(msg) : undefined}
                      onTouchEnd={ENABLE_OUTBOUND_MESSAGE_ACTIONS && canReplyToMessage ? cancelTouchTimer : undefined}
                      onTouchMove={ENABLE_OUTBOUND_MESSAGE_ACTIONS && canReplyToMessage ? cancelTouchTimer : undefined}
                    >
                      <div
                        className={hasWhatsAppTemplate
                          ? 'whatsapp-template-bubble'
                          : `relative max-w-[70%] rounded-2xl px-4 py-3 ${bubbleClasses}`}
                      >
                        {ENABLE_OUTBOUND_MESSAGE_ACTIONS && canReplyToMessage && (
                          <button
                            type="button"
                            className={`message-reply-trigger absolute -top-3 ${isAgentMessage ? 'right-0' : 'left-0'}`}
                            title="Reply to message"
                            aria-label="Reply to message"
                            onClick={(event) => handleReplyClick(event, msg)}
                          >
                            <CornerUpLeft className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {ENABLE_OUTBOUND_MESSAGE_ACTIONS && isReactableInstagramMessage && (
                          <>
                            <button
                              type="button"
                              className={`message-reaction-trigger absolute -top-3 ${isAgentMessage ? 'left-0' : 'right-0'}`}
                              title="React to message"
                              aria-label="React to message"
                              onClick={(event) => {
                                event.stopPropagation();
                                setReactionPickerMessageId((current) => current === msg.id ? null : msg.id);
                              }}
                            >
                              <Smile className="h-3.5 w-3.5" />
                            </button>
                            {reactionPickerMessageId === msg.id && (
                              <div
                                className={`message-reaction-picker absolute z-30 -top-12 ${isAgentMessage ? 'left-0' : 'right-0'}`}
                                role="group"
                                aria-label="Choose a reaction"
                              >
                                {MESSAGE_REACTION_OPTIONS.map((option) => (
                                  <button
                                    key={option.value}
                                    type="button"
                                    className="message-reaction-option"
                                    title={option.label}
                                    aria-label={option.label}
                                    disabled={reactingMessageId === msg.id}
                                    onClick={(event) => handleMessageReaction(event, msg, option.value)}
                                  >
                                    {option.emoji}
                                  </button>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                        {hasReplyContext && (
                          <button
                            type="button"
                            className={`quoted-reply quoted-reply--${replyPlatform} w-full text-left`}
                            onClick={() => handleQuotedMessageJump(replyMetadata.reply_to)}
                            title="Jump to original message"
                          >
                            <span className="quoted-author">{replySenderLabel}</span>
                            <span className="quoted-content">{replyMetadata.reply_preview || 'View original message'}</span>
                          </button>
                        )}
                        {displayText && (
                          <div className="flex items-start gap-2">
                            <p className="text-sm whitespace-pre-line break-words flex-1">{displayText}</p>
                            {phoneMatch && (
                              <button
                                type="button"
                                onClick={() => {
                                  const normalizedMatch = phoneMatch.replace(/[^\d+]/g, '');
                                  const nameParts = splitFullName(chatDisplayName || '');
                                  setCreateInquiryPrefill({
                                    phone: normalizedMatch,
                                    // Keep the inquiry comment consistent with the default modal text
                                    notes: 'Inquiry from Ticklegram chat ',
                                    firstName: nameParts.first,
                                    middleName: nameParts.middle,
                                    lastName: nameParts.last,
                                    email:
                                      chat?.instagram_user?.email ||
                                      chat?.facebook_user?.email ||
                                      '',
                                    city: '',
                                    address: '',
                                  });
                                  setShowInquiryModal(true);
                                }}
                                className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[var(--tg-surface-muted)] text-[var(--tg-text-primary)] border border-[var(--tg-border-soft)] hover:bg-[var(--tg-chat-hover)] transition"
                                title="Create inquiry"
                              >
                                <PhoneCall className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        )}
                        {originLabel && !hasWhatsAppTemplate && (
                          <p className="message-origin text-[11px] italic mt-1">
                            {originLabel}
                          </p>
                        )}
                        {isWhatsAppDeliveryFailure && (
                          <p className="mt-2 flex items-start gap-1.5 text-xs text-red-300" role="alert">
                            <CircleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                            <span>
                              WhatsApp could not deliver this message.
                              {whatsappFailureReason ? ` ${whatsappFailureReason}` : ''}
                            </span>
                          </p>
                        )}
                        {isHumanAgentMessage && (
                          <p className="mt-2 inline-flex items-center rounded-full border border-amber-300/30 bg-amber-300/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-100">
                            Human Agent support response
                          </p>
                        )}
                        {hasStoryMention && (
                          <p className="message-origin italic mt-1 text-purple-200">
                            Mentioned you in their story
                          </p>
                        )}
                        {hasStoryReply && (
                          <p className="message-origin italic mt-1 text-purple-200">
                            Replied to your story
                          </p>
                        )}
                        {attachments.length > 0 && (
                          <div className={hasWhatsAppTemplate ? 'space-y-2' : 'mt-3 space-y-2'}>
                            {attachments.map((attachment, attachmentIndex) => (
                              <MessageAttachment
                                key={`${msg.id || 'msg'}-attachment-${attachmentIndex}`}
                                attachment={attachment}
                                messageId={msg.id}
                                chatId={chat?.id}
                                index={attachmentIndex}
                                onPreview={setMediaPreview}
                              />
                            ))}
                          </div>
                        )}
                        {messageReactions.length > 0 && (
                          <div
                            className={`message-reaction-cluster absolute ${hasWhatsAppTemplate ? 'message-reaction-cluster--template' : 'message-reaction-cluster--standard'} ${isAgentMessage ? 'left-2' : 'right-2'}`}
                            aria-label="Message reactions"
                          >
                            {messageReactions.map((reaction, reactionIndex) => (
                              <span
                                key={`${reaction.actor_id || 'actor'}-${reactionIndex}`}
                                className="message-reaction-badge"
                                title="Reaction"
                              >
                                {reaction.emoji || reaction.reaction}
                              </span>
                            ))}
                          </div>
                        )}
                        <p className={`message-timestamp ${isAgentMessage ? 'message-timestamp--agent' : ''} mt-2 flex items-center gap-1`}>
                          {formatMessageTime(msg.timestamp || (msg.ts ? new Date(msg.ts * 1000).toISOString() : null))}
                          {outboxStatus === 'queued' && <Clock3 className="h-3.5 w-3.5 text-amber-300" aria-label="Queued" />}
                          {outboxStatus === 'sending' && <LoaderCircle className="h-3.5 w-3.5 animate-spin text-sky-300" aria-label="Sending" />}
                          {outboxStatus === 'failed' && <CircleAlert className="h-3.5 w-3.5 text-red-300" aria-label="Not sent" />}
                          {!outboxStatus && chat?.platform === 'WHATSAPP' && isAgentMessage && (() => {
                            const status = String(msg.metadata?.whatsapp_status || 'accepted').toLowerCase();
                            if (status === 'read') return <CheckCheck className="h-3.5 w-3.5 text-sky-300" aria-label="Read" />;
                            if (status === 'delivered') return <CheckCheck className="h-3.5 w-3.5" aria-label="Delivered" />;
                            if (status === 'failed') return (
                              <CircleAlert
                                className="h-3.5 w-3.5 text-red-300"
                                aria-label="Failed"
                                title={whatsappFailureReason || 'WhatsApp could not deliver this message'}
                              />
                            );
                            return <CheckCircle className="h-3.5 w-3.5" aria-label="Sent" />;
                          })()}
                        </p>
                      </div>
                    </div>
                  </React.Fragment>
                );
              });
            })()
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        <div className={`composer-shell ${isMobile ? 'rounded-2xl border border-[var(--tg-border-soft)] pb-safe' : ''}`}>
          {show24hExpiryNotice && (
            <div className="mb-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium">The normal 24-hour response window has expired.</p>
                    {humanAgentEligibility?.feature_mode === 'review' && (
                      <Badge className="border border-amber-300/30 bg-amber-300/10 text-amber-100">
                        Meta App Review
                      </Badge>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-amber-100/80">
                    {humanAgentEligibility?.feature_available
                      ? humanAgentEligibility?.reason
                      : 'Use a Meta-approved template or wait for the customer to send a new message.'}
                  </p>
                </div>
                {humanAgentEligibility?.feature_available && humanAgentEligibility?.eligible && (
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 bg-amber-400 text-slate-950 hover:bg-amber-300"
                    onClick={() => setShowHumanAgentDialog(true)}
                  >
                    Human Agent response
                  </Button>
                )}
              </div>
            </div>
          )}
          {humanAgentSuccess && (
            <div
              className="mb-3 flex items-start gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200"
              role="status"
            >
              <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{humanAgentSuccess}</span>
            </div>
          )}
          {(ENABLE_OUTBOUND_MESSAGE_ACTIONS || isWhatsAppChat) && attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {attachments.map((att, idx) => (
                <span key={`${att.payload?.url || idx}-${idx}`} className="attachment-chip">
                  {att.type}: {att.payload?.url || att.url}
                  <button
                    type="button"
                    className="attachment-chip-remove"
                    onClick={() =>
                      setAttachments((prev) => prev.filter((_, i) => i !== idx))
                    }
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          {ENABLE_OUTBOUND_MESSAGE_ACTIONS && replyTarget && (
            <div className="reply-preview">
              <div className="reply-preview-body">
                <p className="reply-preview-author">{replyTarget.author}</p>
                <p className="reply-preview-text">{replyTarget.snippet}</p>
              </div>
              <button
                type="button"
                className="reply-preview-cancel"
                onClick={handleCancelReply}
                aria-label="Cancel reply"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          <form onSubmit={handleSend} className="space-y-1">
            <div className="relative">
              {quickReplySuggestion && (
                <div ref={quickReplySuggestionRef} aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 overflow-hidden whitespace-pre-wrap break-words rounded-xl px-3 py-2 text-sm leading-5">
                  <span className="invisible">{message}</span>
                  <span className="text-gray-500">{quickReplySuggestionText}</span>
                </div>
              )}
              <Textarea
                ref={composerInputRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onFocus={() => {
                  if (isWhatsAppChat) loadQuickReplies();
                }}
                onKeyDown={(event) => {
                  if (
                    event.key === 'ArrowRight' &&
                    quickReplySuggestion &&
                    event.currentTarget.selectionStart === message.length &&
                    event.currentTarget.selectionEnd === message.length
                  ) {
                    event.preventDefault();
                    applyQuickReply(quickReplySuggestion);
                  }
                }}
                className={`composer-textarea relative z-10 ${quickReplySuggestion ? 'bg-transparent' : ''} ${isMobile ? 'min-h-[40px]' : 'min-h-[44px]'} text-sm resize-none`}
                data-testid="message-input"
                disabled={!canSendManualMessage}
                aria-disabled={!canSendManualMessage}
                placeholder={
                  show24hExpiryNotice
                    ? (isWhatsAppChat
                      ? 'WhatsApp 24-hour service window expired. Wait for a new customer message.'
                      : '24-hour window expired. Use a Meta-approved template.')
                    : 'Type a reply...'
                }
                rows={1}
                style={{ overflow: 'hidden', backgroundColor: quickReplySuggestion ? 'transparent' : undefined }}
                onInput={resizeComposer}
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-1.5">
              {ENABLE_COMPOSER_UTILITIES && (
                <div className="composer-toolbar">
                <div className="relative inline-block" ref={emojiPopoverRef}>
                <Button
                  type="button"
                  onClick={() => setShowEmojiPicker((prev) => !prev)}
                  variant="ghost"
                  className={isMobile ? 'min-w-[36px] min-h-[36px]' : 'h-9 px-3'}
                  title="Insert emoji"
                >
                  <Smile className="w-4 h-4" />
                  <span className="hidden sm:inline text-sm">Emoji</span>
                </Button>
                {showEmojiPicker && (
                  <div className="emoji-popover">
                    {EMOJI_OPTIONS.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        className="emoji-btn"
                        onClick={() => {
                          setMessage((prev) => `${prev}${emoji}`);
                          setShowEmojiPicker(false);
                        }}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}
                {false && showEmojiPicker && (
                  <div className="emoji-popover">
                      {['😀','😃','😄','😁','😆','😅','😂','🙂','😉','😊','😍','😘','😎','🤩','🤔','🤝','👍','🙏','🔥','🎉','❤️','🚀','🌟','😴','🤖','🥳','🤝','📌','✅','❓','☕'].map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          className="emoji-btn"
                          onClick={() => {
                            setMessage((prev) => `${prev}${emoji}`);
                            setShowEmojiPicker(false);
                          }}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {(ENABLE_OUTBOUND_MESSAGE_ACTIONS || isWhatsAppChat) && (
                  <>
                <input
                  ref={mediaInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="hidden"
                  onChange={handleMediaSelected}
                />
                <Button
                  type="button"
                  onClick={() => mediaInputRef.current?.click()}
                  variant="ghost"
                  disabled={isUploadingMedia || !canSendManualMessage}
                  className={isMobile ? 'min-w-[36px] min-h-[36px]' : 'h-9 px-3'}
                  title="Attach image, video, or document"
                >
                  <Paperclip className="w-4 h-4" />
                  <span className="hidden sm:inline text-sm">{isUploadingMedia ? 'Uploading…' : 'Media'}</span>
                </Button>
                  </>
                )}
                <Button
                  type="button"
                  onClick={() => setShowTemplateDialog(true)}
                  variant="ghost"
                  className={isMobile ? 'min-w-[36px] min-h-[36px]' : 'h-9 px-3'}
                  title="Templates (Ctrl+T)"
                >
                  <FileText className="w-4 h-4" />
                  <span className="hidden sm:inline text-sm">Templates</span>
                </Button>
                {isWhatsAppChat && (
                  <div>
                    <Button
                      type="button"
                      onClick={() => {
                        loadQuickReplies();
                        setShowQuickReplyDialog(true);
                      }}
                      variant="ghost"
                      className={isMobile ? 'min-w-[36px] min-h-[36px]' : 'h-9 px-3'}
                      title="Quick replies"
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span className="hidden sm:inline ml-2 text-sm">Quick replies</span>
                    </Button>
                  </div>
                )}
                </div>
              )}
              <Button
                type="submit"
                disabled={!message.trim() || isSending || !canSendManualMessage}
                className={`rounded-full px-4 h-9 bg-gradient-to-r from-indigo-500 to-blue-500 hover:from-indigo-600 hover:to-blue-600 ${
                  isMobile ? 'min-h-[36px]' : ''
                }`}
                data-testid="send-message-button"
              >
                <Send className="w-4 h-4 mr-2" />
                Send
              </Button>
            </div>
          </form>
        </div>

        {/* Template Selector Dialog */}
        <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
          <DialogContent className="bg-[#1a1a2e] border-gray-700 text-white sm:max-w-[1024px] max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Select Template
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Choose a template to send to @{chatHandle || 'unknown'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  {...AUTOFILL_PROPS}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search templates..."
                  className="pl-10 bg-[#0f0f1a] border-gray-700"
                />
              </div>

              {/* Template List */}
              <div className="max-h-[300px] overflow-y-auto space-y-2 pr-2">
                {filteredTemplates.length === 0 ? (
                  <p className="text-center text-gray-400 py-8">
                    {templates.length === 0
                      ? 'No templates available for this platform'
                      : 'No templates match your search'}
                  </p>
                ) : (
                  filteredTemplates.map((template) => (
                    <div
                      key={template.id}
                      onClick={() => handleSelectTemplate(template)}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedTemplate?.id === template.id
                          ? 'border-purple-600 bg-purple-600/10'
                          : 'border-gray-700 hover:border-gray-600 bg-[#0f0f1a]'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-white">{template.name}</h4>
                          {template.is_meta_approved && (
                            <Badge className="bg-purple-600 text-white text-xs">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Utility
                            </Badge>
                          )}
                        </div>
                        <Badge variant="outline" className="border-gray-600 text-xs">
                          {template.category}
                        </Badge>
                        {isWhatsAppChat && template.language && (
                          <Badge variant="outline" className="border-gray-600 text-xs">
                            {template.language}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-400 line-clamp-2">{template.content}</p>
                    </div>
                  ))
                )}
              </div>

              {/* Template Preview & Variables */}
              {selectedTemplate && (
                <div className="space-y-3 border-t border-gray-700 pt-4">
                  <h4 className="font-semibold text-white">Preview & Variables</h4>

                  {/* Variable Inputs */}
                  {Object.keys(templateVariables).length > 0 && (
                    <div className="space-y-2">
                      {isWhatsAppChat && (
                        <p className="text-xs text-amber-200">All WhatsApp template variables are required by Meta.</p>
                      )}
                      {Object.keys(templateVariables).map((varName) => (
                        <div key={varName}>
                          <label className="text-sm text-gray-400 block mb-1">{varName}</label>
                          <Input
                            value={templateVariables[varName]}
                            onChange={(e) =>
                              setTemplateVariables({
                                ...templateVariables,
                                [varName]: e.target.value,
                              })
                            }
                            required={isWhatsAppChat}
                            placeholder={`Enter ${varName}`}
                            className="bg-[#0f0f1a] border-gray-700"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Preview */}
                  <div className="p-3 bg-[#0f0f1a] rounded-lg border border-gray-700">
                    <p className="text-sm text-gray-300 whitespace-pre-wrap">
                      {previewTemplateContent(selectedTemplate)}
                    </p>
                  </div>

                  {/* Send Button */}
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedTemplate(null);
                        setTemplateVariables({});
                      }}
                    >
                      Clear
                    </Button>
                    <Button
                      onClick={handleSendTemplate}
                      disabled={
                        isSending ||
                        (show24hExpiryNotice && !selectedTemplate?.is_meta_approved)
                      }
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send Template
                    </Button>
                  </div>
                  {show24hExpiryNotice && !selectedTemplate?.is_meta_approved && (
                    <p className="text-xs text-amber-300 text-right">
                      Meta-approved templates are required outside the 24-hour window.
                    </p>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={showQuickReplyDialog} onOpenChange={setShowQuickReplyDialog}>
          <DialogContent className="bg-[#1a1a2e] border-gray-700 text-white sm:max-w-2xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Quick replies
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Select a saved reply to insert into this WhatsApp conversation.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[55vh] space-y-2 overflow-y-auto pr-2">
              {quickReplies.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-400">No quick replies are available yet.</p>
              ) : quickReplies.map((reply) => (
                <button
                  key={reply.id}
                  type="button"
                  onClick={() => applyQuickReply(reply)}
                  className="w-full rounded-lg border border-gray-700 bg-[#0f0f1a] p-3 text-left transition-colors hover:border-purple-500 hover:bg-purple-500/10"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium text-white">{reply.name}</span>
                    <code className="shrink-0 text-xs text-emerald-300">{reply.shortcut}</code>
                  </div>
                  <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-sm text-gray-400">{reply.content}</p>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
        <Dialog
          open={showHumanAgentDialog}
          onOpenChange={(open) => {
            setShowHumanAgentDialog(open);
            if (!open && !isSendingHumanAgent) {
              setHumanAgentContent('');
              setHumanAgentReason('');
              setHumanAgentAcknowledged(false);
            }
          }}
        >
          <DialogContent className="border-amber-300/25 bg-[#151a29] text-white sm:max-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-amber-300" />
                Human Agent response
              </DialogTitle>
              <DialogDescription className="text-slate-300">
                Send one manual, one-to-one reply for an unresolved customer-support case between 24 hours and seven days after the customer's last message. Never use this for marketing, campaigns, re-engagement, or automated follow-ups.
              </DialogDescription>
            </DialogHeader>
            <form className="space-y-3.5" onSubmit={handleSendHumanAgentMessage}>
              <div className="rounded-lg border border-amber-300/20 bg-amber-300/5 px-3 py-2 text-xs text-amber-100">
                <p className="font-medium">Eligibility verified by the server</p>
                <p className="mt-1 text-amber-100/75">
                  Customer message: {humanAgentEligibility?.last_customer_message_at
                    ? formatMessageFullDateTime(humanAgentEligibility.last_customer_message_at)
                    : 'Not available'}
                  {' · '}
                  Human Agent window expires: {humanAgentEligibility?.expires_at
                    ? formatMessageFullDateTime(humanAgentEligibility.expires_at)
                    : 'Not available'}
                </p>
              </div>
              <div className="space-y-1.5">
                <label htmlFor="human-agent-reason" className="text-sm font-medium text-slate-100">Support reason</label>
                <Textarea
                  id="human-agent-reason"
                  value={humanAgentReason}
                  onChange={(event) => setHumanAgentReason(event.target.value)}
                  placeholder="E.g. Weekend closure delayed resolution of the customer's enquiry."
                  className="min-h-[64px] border-slate-600 bg-slate-950/60 text-white"
                  maxLength={500}
                  required
                />
                <p className="text-xs text-slate-400">Required for the support audit record.</p>
              </div>
              <div className="space-y-1.5">
                <label htmlFor="human-agent-content" className="text-sm font-medium text-slate-100">Reply</label>
                <Textarea
                  id="human-agent-content"
                  value={humanAgentContent}
                  onChange={(event) => setHumanAgentContent(event.target.value)}
                  placeholder="Write a personal support response…"
                  className="min-h-[108px] border-slate-600 bg-slate-950/60 text-white"
                  maxLength={2000}
                  required
                />
              </div>
              <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-200">
                <input
                  type="checkbox"
                  checked={humanAgentAcknowledged}
                  onChange={(event) => setHumanAgentAcknowledged(event.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-500 accent-amber-400"
                />
                <span>I confirm this is an individual, manually written support response for this unresolved customer case.</span>
              </label>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowHumanAgentDialog(false)} disabled={isSendingHumanAgent}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-amber-400 text-slate-950 hover:bg-amber-300"
                  disabled={!humanAgentContent.trim() || !humanAgentReason.trim() || !humanAgentAcknowledged || isSendingHumanAgent}
                >
                  <Send className="mr-2 h-4 w-4" />
                  {isSendingHumanAgent ? 'Sending…' : 'Send support response'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        <CreateInquiryModal
          isOpen={showInquiryModal}
          onClose={() => setShowInquiryModal(false)}
          onSubmit={() => setShowInquiryModal(false)}
          chat={chat}
          chatDisplayName={chatDisplayName}
          showAssignmentInfo={showAssignmentInfo}
          selectChat={selectChat}
          prefillData={createInquiryPrefill}
        />
        <Dialog open={Boolean(mediaPreview)} onOpenChange={(open) => !open && setMediaPreview(null)}>
          <DialogContent className="max-h-[94vh] max-w-[96vw] overflow-hidden border-[var(--tg-border-soft)] bg-[#0b1020] p-3 text-white sm:max-w-5xl">
            <DialogHeader className="sr-only">
              <DialogTitle>{mediaPreview?.alt || 'Image preview'}</DialogTitle>
            </DialogHeader>
            {mediaPreview?.src && (
              <img
                src={mediaPreview.src}
                alt={mediaPreview.alt || 'Image preview'}
                className="max-h-[86vh] w-full rounded-lg object-contain"
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
      {(isMobile || isTablet) && isProfileOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm">
          <div className="absolute inset-0" onClick={handleCloseProfile} role="presentation" />
          <div className="absolute inset-x-3 bottom-3 top-12 overflow-y-auto">
          <ChatProfilePanel
            chat={chat}
            orderedMessages={orderedMessages}
            onClose={handleCloseProfile}
            isMobile
            canSendManualMessage={canSendManualMessage}
            chatDisplayName={chatDisplayName}
            chatHandle={chatHandle}
            chatAvatarUrl={chatAvatarUrl}
            lastActivityLabel={lastActivityLabel}
            showAssignmentInfo={showAssignmentInfo}
          />
                </div>
              </div>
      )}
      {!isMobile && !isTablet && (
        <aside
          className={cn(
            'flex-col h-full min-w-[280px] max-w-[340px] flex-shrink-0 transition-all duration-150',
            isProfileOpen ? 'hidden lg:flex opacity-100 translate-x-0' : 'hidden lg:hidden'
          )}
        >
          <ChatProfilePanel
            chat={chat}
            orderedMessages={orderedMessages}
            onClose={handleCloseProfile}
            isMobile={false}
            canSendManualMessage={canSendManualMessage}
            chatDisplayName={chatDisplayName}
            chatHandle={chatHandle}
            chatAvatarUrl={chatAvatarUrl}
            lastActivityLabel={lastActivityLabel}
            showAssignmentInfo={showAssignmentInfo}
          />
        </aside>
      )}
    </div>
  );
};

const ChatProfilePanel = ({
  chat,
  orderedMessages = [],
  onClose,
  isMobile,
  canSendManualMessage,
  chatDisplayName,
  chatHandle,
  chatAvatarUrl,
  lastActivityLabel,
  showAssignmentInfo = true,
}) => {
  const assignedAgent = chat.assigned_agent;
  const messageCount = orderedMessages.length;
  const lastMessage = messageCount > 0 ? orderedMessages[messageCount - 1] : null;

  const displayName = chatDisplayName || getChatDisplayName(chat);
  const handle = chatHandle || getChatHandle(chat);
  const avatarUrl =
    chatAvatarUrl ||
    chat.profile_pic_url ||
    chat.profile_pic ||
    null;

  const statusLabel = chat.status
    ? chat.status
        .split(/[_\s]+/)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
    : 'Unknown';

  const statusBadgeClass =
    chat.status === 'assigned'
      ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/40'
      : 'bg-amber-500/10 text-amber-300 border-amber-500/40';

  const platformLabel = chat.platform === 'WHATSAPP' ? 'WhatsApp' : chat.platform === 'FACEBOOK' ? 'Facebook' : 'Instagram';
  const infoItems = [
    {
      label: 'Handle',
      value: `@${handle || 'unknown'}`,
    },
    ...(showAssignmentInfo
      ? [
          {
            label: 'Assigned Agent',
            value: assignedAgent?.name || 'Unassigned',
          },
          {
            label: 'Status',
            value: statusLabel,
          },
        ]
      : []),
    {
      label: 'Last Activity',
      value: lastActivityLabel || 'Not available',
    },
    {
      label: 'Chat Created',
      value: chat.created_at ? formatMessageDate(chat.created_at) : 'Not available',
    },
    {
      label: 'Last Updated',
      value: chat.updated_at ? formatMessageDate(chat.updated_at) : 'Not available',
    },
    {
      label: 'Total Messages',
      value: messageCount,
    },
    {
      label: 'Unread Messages',
      value: chat.unread_count ?? 0,
    },
    ...(showAssignmentInfo && chat.instagram_user_id
      ? [
          {
            label: 'Instagram User ID',
            value: chat.instagram_user_id,
          },
        ]
      : []),
    ...(showAssignmentInfo && chat.facebook_page_id
      ? [
          {
            label: 'Facebook Page ID',
            value: chat.facebook_page_id,
          },
        ]
      : []),
    ...(showAssignmentInfo
      ? [
          {
            label: 'Chat ID',
            value: chat.id,
          },
          {
            label: 'Human Agent Window',
            value: canSendManualMessage ? 'Active (within 24 hours)' : 'Expired',
          },
        ]
      : []),
  ];

  return (
    <aside
      className={cn(
        'chat-panel bg-[var(--tg-surface)] border border-[var(--tg-border-soft)] flex flex-col min-h-0',
        isMobile ? 'rounded-2xl' : 'h-full overflow-hidden'
      )}
    >
      <div className="p-2 border-b border-[var(--tg-border-soft)] flex items-start justify-between">
        <div className="flex items-center gap-2">
          <ChatAvatar src={avatarUrl} alt={displayName} />
          <div className="min-w-0">
            <p className="text-[10px] uppercase text-[var(--tg-text-muted)] tracking-wide">Profile</p>
            <p className="text-base font-semibold text-[var(--tg-text-primary)] truncate">{displayName}</p>
            <p className="text-xs text-[var(--tg-text-muted)] truncate">@{handle || 'unknown'}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {showAssignmentInfo && <Badge className={statusBadgeClass}>{statusLabel}</Badge>}
              <Badge variant="outline" className="border-[var(--tg-border-soft)] text-[var(--tg-text-secondary)]">
                {platformLabel}
              </Badge>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-[var(--tg-text-muted)] hover:text-[var(--tg-text-primary)] transition-colors h-8 w-8 flex items-center justify-center rounded-full border border-transparent hover:border-[var(--tg-border-soft)]"
          aria-label="Close profile panel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2.5 chat-scroll">
        {chat.platform === 'WHATSAPP' && hasPermission(currentUser, 'whatsapp:bot:manage') && <BotContactPanel key={chat.id} chat={chat} />}
        <div className="space-y-1.5">
          <p className="text-xs uppercase text-[var(--tg-text-muted)] tracking-wide">Conversation Info</p>
          <div className="grid grid-cols-2 gap-x-3 gap-y-2">
            {infoItems.map((item) => (
              <div key={item.label} className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-[var(--tg-text-muted)]">{item.label}</span>
                <span className="text-sm text-[var(--tg-text-primary)] break-all">{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <p className="text-xs uppercase text-[var(--tg-text-muted)] tracking-wide">Last Message</p>
          {lastMessage ? (
            <div className="bg-[var(--tg-surface-muted)] border border-[var(--tg-border-soft)] rounded-lg p-2.5">
              <p className="text-sm text-[var(--tg-text-primary)] whitespace-pre-wrap">{lastMessage.content}</p>
              <p className="text-xs text-[var(--tg-text-muted)] mt-1.5">
                {lastMessage.timestamp ? formatMessageDate(lastMessage.timestamp) : ''}
              </p>
            </div>
          ) : (
            <p className="text-sm text-[var(--tg-text-muted)]">No messages yet</p>
          )}
        </div>

      </div>
    </aside>
  );
};

const MessageSquareIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

export default ChatWindow;



